import { Dispatch, SetStateAction, useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import { ChatBubbleOutlineOutlined, EmailOutlined, MobileFriendlyOutlined, PhoneOutlined } from '@mui/icons-material'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material'

import ContactDialog from '../../../components/contact-dialog'
import PlainTooltip from '../../../components/m3/plain-tooltip'
import StickyHeader from '../../../components/sticky-header'
import { AuthenticationContext } from '../../../providers/auth-provider'
import { formatPhoneNumber } from '../../../utils/helpers'
import useIsMobile from '../../../utils/hooks/useIsMobile'
import { hasEditUsersPermission } from '../../../utils/permission-helpers'
import ContactsContext from '../context'

import DeleteUserModal from './delete-user-modal'
import useStyles from './index.styles'

interface IProps {
  user: IUserType
  selectedFolderId: number | undefined
  setOpen?: Dispatch<SetStateAction<boolean>>
}

const UserDetails = (props: IProps) => {
  const { user, selectedFolderId, setOpen } = props
  const {
    id: userId,
    information: { first: firstName, last: lastName },
    email,
    phone,
  } = user
  const preferences = user.preferences?.notification || []

  const classes = useStyles
  const theme = useTheme()
  const isMobile = useIsMobile()

  const navigate = useNavigate()

  const [openContactForm, setOpenContactForm] = useState<boolean>(false)

  const { getNotificationContacts, notificationContacts } = useContext(ContactsContext)
  const { ownUser, ownPermissions } = useContext(AuthenticationContext)

  const hasModifyPermission = hasEditUsersPermission(ownPermissions)

  const isFirstContact = notificationContacts.length > 0 && notificationContacts[0].user.id === userId
  const canDelete = ownUser?.id !== userId && notificationContacts.length > 1 && !isFirstContact

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showEditContactTooltip, setShowEditContactTooltip] = useState<boolean>(false)
  const isEditDisabled = !selectedFolderId

  return (
    <>
      <StickyHeader zIndex={1300} includeTopCover={true} fullBleed={!isMobile} sx={isMobile ? classes.mobileUserHeader : undefined}>
        <Grid container={true} sx={classes.userDetailsContainer}>
          <Grid size={12} sx={classes.nameRow}>
            <Typography variant="h6" sx={classes.userName} className="fs-exclude">
              {firstName} {lastName}
            </Typography>

            {hasModifyPermission && (
              <Box>
                <PlainTooltip
                  title="You can edit your notification preferences and other contact information after selecting an account."
                  open={isEditDisabled && showEditContactTooltip}
                  onClose={() => setShowEditContactTooltip(false)}
                  disableFocusListener
                  disableTouchListener
                >
                  <span onMouseEnter={() => isEditDisabled && setShowEditContactTooltip(true)} onMouseLeave={() => setShowEditContactTooltip(false)}>
                    <IconButton disabled={isEditDisabled} variant="contained" onClick={() => setOpenContactForm(true)} sx={classes.editBtn} aria-label="Edit">
                      <CreateOutlinedIcon />
                    </IconButton>
                  </span>
                </PlainTooltip>

                {canDelete && selectedFolderId && (
                  <IconButton disabled={!selectedFolderId} variant="contained" onClick={() => setShowDeleteModal(true)} data-testid="delete-contact">
                    <DeleteOutlineOutlinedIcon color="error" />
                  </IconButton>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </StickyHeader>

      <Box sx={classes.contactDetailsCard(theme, isMobile)}>
        <Grid size={12} sx={classes.contactInfoHeader}>
          <Typography variant="caption" color="textSecondary" sx={classes.contactInfoLabel}>
            Contact Information
          </Typography>
        </Grid>

        {phone && (
          <Grid container={true} sx={classes.phoneContainer}>
            <Grid>
              <PhoneOutlined color="secondary" />
            </Grid>

            <Grid sx={classes.phoneDetails}>
              <Typography variant="body1" className="fs-exclude">
                {phone && formatPhoneNumber(phone)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Mobile
              </Typography>
            </Grid>
          </Grid>
        )}

        <Grid container={true} sx={classes.emailContainer}>
          <Grid>
            <EmailOutlinedIcon color="secondary" />
          </Grid>

          <Grid sx={classes.emailDetails}>
            <Typography variant="body1" className="fs-exclude" sx={classes.email}>
              {email}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Email
            </Typography>
          </Grid>
        </Grid>

        <Grid size={12} sx={classes.preferencesContainer}>
          <Typography variant="caption" color="textSecondary" sx={classes.contactInfoLabel}>
            Notification preferences
          </Typography>

          <Grid container={true} sx={classes.preferencesIconContainer}>
            {preferences.length === 0 && <Typography variant="body2">Do not contact</Typography>}
            {preferences.includes('phone') && <PhoneOutlined htmlColor={theme.palette.text.secondary} aria-label="phone" />}
            {preferences.includes('email') && <EmailOutlined htmlColor={theme.palette.text.secondary} aria-label="email" />}
            {preferences.includes('sms') && <ChatBubbleOutlineOutlined htmlColor={theme.palette.text.secondary} aria-label="sms" />}
            {preferences.includes('push') && <MobileFriendlyOutlined htmlColor={theme.palette.text.secondary} aria-label="push" />}
          </Grid>
        </Grid>
      </Box>

      {selectedFolderId && (
        <DeleteUserModal
          open={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          userId={userId}
          closeUserDetailsDrawer={() => (setOpen ? setOpen(false) : navigate(-1))}
          selectedFolderId={selectedFolderId}
        />
      )}

      {selectedFolderId && (
        <ContactDialog
          selectedFolderId={selectedFolderId}
          openContactForm={openContactForm}
          handleCloseContactForm={() => setOpenContactForm(false)}
          reload={getNotificationContacts}
          editUser={user}
        />
      )}
    </>
  )
}

export default UserDetails
